import { translateBackend } from '@assets/i18n/config';
import type { AssetClassBranch } from '@interfaces/financialProduct';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import {
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { round, sumBy } from 'lodash-es';
import { type FC, Fragment } from 'react';
import {
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  Droppable,
  type DroppableProvided,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import type { DragProps, Security, SecurityTableOptions } from './types';
import { COLUMNS, findSecurityIndex } from './types';

// Style constants
const GROUP_HEADER_ROW_BG = { bgcolor: 'grey.50' } as const;
const UNASSIGNED_ROW_BG = { bgcolor: 'warning.light' } as const;
const HEADER_CELL_STYLE = {
  fontWeight: 600,
  fontSize: '14px',
  fontFamily: 'Interphases Pro',
  py: 1,
} as const;
const TARGET_PERCENTAGE_STYLE = {
  fontSize: '14px',
  fontWeight: 500,
} as const;
const ADD_BUTTON_STYLE = {
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': { opacity: 0.8 },
  color: 'primary.main',
  display: 'inline-block',
} as const;
const DISABLED_CURSOR = 'not-allowed' as const;
const ENABLED_CURSOR = 'pointer' as const;
const HOVER_OPACITY_DISABLED = 1 as const;
const HOVER_OPACITY_ENABLED = 0.8 as const;
const DISABLED_COLOR = 'text.disabled' as const;
const ENABLED_COLOR = 'primary.main' as const;

export const TableHeaders: FC<{
  options?: SecurityTableOptions;
  sortField: string;
  sortDesc: boolean;
  onSort: (field: string) => void;
  sortable?: boolean;
  isGroupedMode?: boolean;
}> = ({
  options,
  sortField,
  sortDesc,
  onSort,
  sortable = false,
  isGroupedMode = false,
}) => {
  const { t } = useTranslation(['modelPortfolios']);
  return (
    <TableRow testId='securities-selection-table-header-row'>
      {isGroupedMode && (
        <TableHeadCell
          testId='securities-selection-table-header-assetClassName'
          sx={{ width: '20%' }}
        >
          {translateBackend(
            options?.securityAllocationChart?.columns?.[0]?.assetClass?.label,
          ) || t('assetClass')}
        </TableHeadCell>
      )}
      {Object.values(COLUMNS).map((column) => {
        if (isGroupedMode && column.key === COLUMNS.ASSET_CLASS.key) {
          return null;
        }

        const columnConfig =
          options?.securityAllocationChart?.columns?.[0]?.[column.key];

        if (!columnConfig?.enabled) {
          return null;
        }

        const isSortable = column.sortKey && sortable && !isGroupedMode;
        const isSorted = sortField === column.sortKey;

        let sortDirection: 'desc' | 'asc' | undefined;
        if (isSorted) {
          sortDirection = sortDesc ? 'desc' : 'asc';
        }

        return (
          <TableHeadCell
            testId={`securities-selection-table-header-${column.key}`}
            key={column.key}
            isSortable={!!isSortable}
            onClick={
              isSortable && column.sortKey
                ? () => onSort(column.sortKey)
                : undefined
            }
            sortDirection={sortDirection}
            sx={{ width: column.width }}
          >
            {translateBackend(columnConfig.label)}
          </TableHeadCell>
        );
      })}
    </TableRow>
  );
};

type GroupedTableBodyProps = {
  groupedByAssetClass: Array<{
    branch: AssetClassBranch;
    securities: Security[];
  }>;
  unassignedSecurities: Security[];
  initialSecurities: Security[];
  draggableOffsets: number[];
  editable: boolean;
  cashAlreadyExists: boolean;
  options?: SecurityTableOptions;
  sortField: string;
  sortDesc: boolean;
  onSort: (field: string) => void;
  sortable: boolean;
  onAddSecurity?: () => void;
  onAddCash?: () => void;
  renderSecurityRow: (
    security: Security,
    globalIndex: number,
    dragProps?: DragProps,
  ) => JSX.Element;
};

export const GroupedTableBody: FC<GroupedTableBodyProps> = ({
  groupedByAssetClass,
  unassignedSecurities,
  initialSecurities,
  draggableOffsets,
  editable,
  cashAlreadyExists,
  options,
  sortField,
  sortDesc,
  onSort,
  sortable,
  onAddSecurity,
  onAddCash,
  renderSecurityRow,
}) => {
  const { t } = useTranslation(['modelPortfolios']);

  return (
    // Single Droppable/TableBody keeps all content in one <tbody> so column
    // widths are calculated consistently. Sequential Draggable indices span
    // across groups; cross-group drops are prevented in handleDragEnd.
    <Droppable droppableId='securities-table-grouped'>
      {(provided: DroppableProvided) => (
        <TableBody {...provided.droppableProps} ref={provided.innerRef}>
          <TableHeaders
            options={options}
            sortField={sortField}
            sortDesc={sortDesc}
            onSort={onSort}
            sortable={sortable}
            isGroupedMode
          />

          {groupedByAssetClass.map(
            ({ branch, securities: groupSecs }, groupMapIdx) => {
              const groupOffset = draggableOffsets[groupMapIdx] ?? 0;
              const assetClassName =
                translateBackend(branch.assetClass?.translatedName) ||
                branch.assetClass?.name ||
                '';
              const groupTotal = round(sumBy(groupSecs, 'percentage'), 2);
              const isGroupValid = groupTotal === 100;

              const rows = groupSecs.map((security, groupIdx) => {
                const currentIndex = groupOffset + groupIdx;
                const globalIndex = findSecurityIndex(
                  initialSecurities,
                  security,
                );

                return (
                  <Draggable
                    key={
                      security.financialProduct.id || `security-${globalIndex}`
                    }
                    draggableId={
                      security.financialProduct.id || `security-${globalIndex}`
                    }
                    index={currentIndex}
                    isDragDisabled={!editable}
                  >
                    {(
                      dragProvided: DraggableProvided,
                      dragSnapshot: DraggableStateSnapshot,
                    ) =>
                      renderSecurityRow(security, globalIndex, {
                        innerRef: dragProvided.innerRef,
                        draggableProps: dragProvided.draggableProps,
                        dragHandleProps: editable
                          ? dragProvided.dragHandleProps
                          : undefined,
                        isDragging: dragSnapshot.isDragging,
                      })
                    }
                  </Draggable>
                );
              });

              return (
                <Fragment key={branch.assetClass?.id ?? branch.id}>
                  <TableRow
                    data-testid={`asset-class-group-header-${branch.assetClass?.id}`}
                    sx={GROUP_HEADER_ROW_BG}
                  >
                    <TableCell align='left' sx={HEADER_CELL_STYLE}>
                      {assetClassName}
                    </TableCell>
                    <TableCell colSpan={2} sx={{ py: 1 }} />
                    <TableCell align='right' sx={{ py: 1, minWidth: '100px' }}>
                      <Typography
                        sx={{
                          color: isGroupValid ? 'success.main' : 'error.main',
                          ...TARGET_PERCENTAGE_STYLE,
                        }}
                      >
                        {branch.targetPercentage ?? 0}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {rows}
                </Fragment>
              );
            },
          )}

          {unassignedSecurities.length > 0 && (
            <Fragment key='__unassigned__'>
              <TableRow sx={UNASSIGNED_ROW_BG}>
                <TableCell colSpan={4} sx={HEADER_CELL_STYLE}>
                  {t('modelPortfolios:unassigned')}
                </TableCell>
              </TableRow>
              {/* Unassigned securities are intentionally not wrapped in
                    <Draggable> — they cannot be reordered until the user
                    assigns an asset class, which unblocks form submission. */}
              {unassignedSecurities.map((security) => {
                const globalIndex = findSecurityIndex(
                  initialSecurities,
                  security,
                );
                return renderSecurityRow(security, globalIndex);
              })}
            </Fragment>
          )}

          {provided.placeholder}

          {editable && (onAddSecurity || onAddCash) && (
            <TableRow>
              <TableCell colSpan={4}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {onAddSecurity && (
                    <Typography
                      data-testid='add-security-button'
                      sx={ADD_BUTTON_STYLE}
                      onClick={onAddSecurity}
                    >
                      {t('modelPortfolios:addSecurity')}
                    </Typography>
                  )}
                  {onAddCash && (
                    <Typography
                      data-testid='add-cash-button'
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: cashAlreadyExists
                          ? DISABLED_CURSOR
                          : ENABLED_CURSOR,
                        '&:hover': {
                          opacity: cashAlreadyExists
                            ? HOVER_OPACITY_DISABLED
                            : HOVER_OPACITY_ENABLED,
                        },
                        color: cashAlreadyExists
                          ? DISABLED_COLOR
                          : ENABLED_COLOR,
                        display: 'inline-block',
                      }}
                      onClick={cashAlreadyExists ? undefined : onAddCash}
                      aria-disabled={cashAlreadyExists}
                    >
                      {t('modelPortfolios:addCash')}
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      )}
    </Droppable>
  );
};
